'use client';

import { useEffect, useRef } from 'react';
import { registrarEvento } from '@/services/googleAnalytics';
import { Produto } from '@/types/produto';

interface UseRowScrollTrackerProps {
  items: Produto[];
  itemsPerRow: number;
  eventName: string;
  getRowData?: (
    rowItems: Produto[],
    rowIndex: number,
  ) => { linha: number; produtos: string[] };
  visibilityThreshold?: number;
  enabled?: boolean;
}

export function useRowScrollTracker({
  items,
  itemsPerRow,
  eventName,
  getRowData,
  visibilityThreshold = 0.5,
  enabled = true,
}: UseRowScrollTrackerProps) {
  const viewedRows = useRef(new Set<number>());
  const observers = useRef<Map<number, IntersectionObserver>>(new Map());

  const registerElement = (itemIndex: number, element: HTMLElement | null) => {
    if (!element || !enabled) return;

    // Calculate which row this item belongs to
    const rowIndex = Math.floor(itemIndex / itemsPerRow);

    // Skip if we already have an observer for this row
    if (observers.current.has(rowIndex)) return;

    // Get all items in this row
    const rowStartIndex = rowIndex * itemsPerRow;
    const rowEndIndex = Math.min(rowStartIndex + itemsPerRow, items.length);
    const rowItems = items.slice(rowStartIndex, rowEndIndex);

    // Create observer for this row
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewedRows.current.has(rowIndex)) {
            viewedRows.current.add(rowIndex);

            // Prepare event data
            const eventData = getRowData
              ? getRowData(rowItems, rowIndex)
              : {
                  linha: rowIndex + 1, // 1-based for analytics
                };

            // Send analytics event
            registrarEvento(eventName, {
              ...eventData,
              total_de_itens: items.length,
              itens_por_linha: rowItems.length,
              total_de_linhas: Math.ceil(items.length / itemsPerRow),
            });
          }
        });
      },
      {
        threshold: visibilityThreshold,
        rootMargin: '0px',
      },
    );

    observer.observe(element);
    observers.current.set(rowIndex, observer);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observers.current.forEach((observer) => observer.disconnect());
      observers.current.clear();
    };
  }, []);

  // Reset when items change
  useEffect(() => {
    viewedRows.current.clear();
    observers.current.forEach((observer) => observer.disconnect());
    observers.current.clear();
  }, [items.length, itemsPerRow]);

  return { registerElement };
}
